import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./OrdersPage.css";

export default function OrdersPage() {
  const token = localStorage.getItem("token");
  const API_BASE = "https://api.goharesadaf.ir/api";

  // data
  const deliveryMap = {
  post: "پست پیشتاز",
  express: "تیپاکس",
  snapp: "اسنپ",
};
const statusMap = {
  paid: "پرداخت شده",
  pending: "در انتظار پرداخت",
  initiated: "شروع شده",
  failed: "ناموفق",
  canceled: "لغو شده",
  completed: "تکمیل شده",
  shipped: "ارسال شده",
  processing: "در حال پردازش",
};
  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(null); // if server provides meta
  const [loading, setLoading] = useState(true);

  // UI state & filters
  const [q, setQ] = useState(""); // general search
  const [status, setStatus] = useState(""); // single status filter
  const [delivery, setDelivery] = useState(""); // delivery_option
  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minTotal, setMinTotal] = useState("");
  const [maxTotal, setMaxTotal] = useState("");
  const [sortBy, setSortBy] = useState("created_at"); // created_at, total, status
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);

  // UI helpers
  const [viewMode, setViewMode] = useState("cards"); // cards | list
  const [expandedFilters, setExpandedFilters] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllOnPage, setSelectAllOnPage] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // debounce search
  const searchTimer = useRef(null);
  useEffect(() => {
    // only debounce q
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchOrders(); // immediate fetch after debounce
    }, 400);
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // fetch on mount and when query filters change (except q handled above)
  useEffect(() => {
    // keep q debounce triggered separately; here include other filters
    const abort = fetchOrders();
    return () => abort && abort.cancel && abort.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, delivery, province, city, dateFrom, dateTo, minTotal, maxTotal, sortBy, sortDir, page, perPage]);

  // build axios cancel token wrapper
  const lastCancelSource = useRef(null);
  async function fetchOrders() {
    setLoading(true);
    setSelectedIds(new Set());
    setSelectAllOnPage(false);

    // cancel previous
    if (lastCancelSource.current) lastCancelSource.current.cancel("new request");
    lastCancelSource.current = axios.CancelToken.source();

    const params = {
      q: q || undefined,
      status: status || undefined,
      delivery_option: delivery || undefined,
      province: province || undefined,
      city: city || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      min_total: minTotal || undefined,
      max_total: maxTotal || undefined,
      sort_by: sortBy || undefined,
      sort_dir: sortDir || undefined,
      page,
      per_page: perPage,
    };

    try {
      const res = await axios.get(`${API_BASE}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        cancelToken: lastCancelSource.current.token,
      });

      // backends vary: try structured response first
      let respData = res.data;
      if (respData && Array.isArray(respData.data)) {
        setOrders(respData.data); // <-- use the "data" field from API
        setTotalCount(respData.total || respData.data.length);
      } else {
        setOrders([]);
        setTotalCount(0);
      }
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error(err);
        toast.error("خطا در بارگذاری سفارش‌ها");
      }
    } finally {
      setLoading(false);
    }
  }

  // update a single order in-place (after admin change)
  const updateStatus = async (id, newStatus) => {
    try {
      const res = await axios.put(
        `${API_BASE}/orders/${id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = res.data;
      setOrders((prev) => prev.map((o) => (o.id === id ? updated : o)));
      toast.success("وضعیت سفارش تغییر کرد");
    } catch (err) {
      console.error(err);
      toast.error("خطا در تغییر وضعیت سفارش");
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("آیا مطمئن هستید که می‌خواهید این سفارش را حذف کنید؟")) return;
    try {
      await axios.delete(`${API_BASE}/orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders((prev) => prev.filter((o) => o.id !== id));
      toast.success("سفارش حذف شد");
    } catch (err) {
      console.error(err);
      toast.error("خطا در حذف سفارش");
    }
  };

  // bulk actions
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  const toggleSelectAll = () => {
    if (selectAllOnPage) {
      setSelectedIds(new Set());
      setSelectAllOnPage(false);
    } else {
      const s = new Set(orders.map((o) => o.id));
      setSelectedIds(s);
      setSelectAllOnPage(true);
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return toast.info("هیچ سفارشی انتخاب نشده است.");
    if (!window.confirm(`آیا مطمئن هستید که می‌خواهید ${selectedIds.size} سفارش را حذف کنید؟`)) return;
    setBulkActionLoading(true);
    try {
      // naive approach: call delete per id (or create new admin endpoint for batch)
      const ids = Array.from(selectedIds);
      await Promise.all(ids.map((id) => axios.delete(`${API_BASE}/orders/${id}`, { headers: { Authorization: `Bearer ${token}` } })));
      setOrders((prev) => prev.filter((o) => !selectedIds.has(o.id)));
      setSelectedIds(new Set());
      toast.success("سفارش‌ها حذف شدند.");
    } catch (err) {
      console.error(err);
      toast.error("خطا در حذف گروهی سفارشات");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkSetStatus = async (newStatus) => {
    if (selectedIds.size === 0) return toast.info("هیچ سفارشی انتخاب نشده است.");
    if (!window.confirm(`آیا مطمئن هستید که می‌خواهید وضعیت ${selectedIds.size} سفارش را به "${newStatus}" تغییر دهید؟`)) return;
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedIds);
      // if you have a batch endpoint use it; otherwise do parallel calls
      await Promise.all(ids.map((id) => axios.put(`${API_BASE}/orders/${id}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } })));
      // refresh page of orders (safer)
      fetchOrders();
      setSelectedIds(new Set());
      toast.success("عملیات گروهی انجام شد.");
    } catch (err) {
      console.error(err);
      toast.error("خطا در تغییر وضعیت گروهی");
    } finally {
      setBulkActionLoading(false);
    }
  };

  // CSV export
  const exportCSV = () => {
    if (!orders || orders.length === 0) return toast.info("سفارشی برای خروجی وجود ندارد.");
    const rows = [
      ["id", "user_name", "user_email", "status", "delivery_option", "subtotal", "shipping_cost", "total", "created_at", "items_count"],
    ];
    orders.forEach((o) => {
      rows.push([
        o.id,
        `"${(o.user?.name || "").replace(/"/g, '""')}"`,
        o.user?.email || "",
        o.status || "",
        o.delivery_option || "",
        Number(o.subtotal || 0),
        Number(o.shipping_cost || 0),
        Number(o.total || 0),
        o.created_at || "",
        (o.items || []).length,
      ]);
    });
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders_export_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // pagination helpers (server-side preferred)
  const lastPage = useMemo(() => {
    if (!totalCount) return Math.max(1, Math.ceil(orders.length / perPage));
    return Math.max(1, Math.ceil(totalCount / perPage));
  }, [totalCount, orders.length, perPage]);

  // format helper
  const fmt = (n) => Number(n || 0).toLocaleString("fa-IR");

  // UI render
  return (
    <div className="orders-container admin">
      <header className="orders-top">
        <h1>مدیریت سفارش‌ها</h1>

        <div className="top-actions">
          <div className="view-switch">
            <button onClick={() => setViewMode("cards")} className={viewMode === "cards" ? "active" : ""}>کارت</button>
            <button onClick={() => setViewMode("list")} className={viewMode === "list" ? "active" : ""}>لیست</button>
          </div>

          <div className="quick-actions">
            <button onClick={() => { 
              setQ(""); setStatus(""); setDelivery(""); setCity(""); setProvince(""); setDateTo(""); setDateFrom(""); 
              setMinTotal(""); setMaxTotal(""); setSortBy(""); setSortDir(""); setPerPage(15);
              setPage(1); fetchOrders(); }} className="btn-quiet">ریست فیلتر</button>
            <button onClick={exportCSV} className="btn-primary">خروجی اکسل</button>
          </div>
        </div>
      </header>

      <section className={`filters ${expandedFilters ? "expanded" : "collapsed"}`}>
        <div className="filters-row">
          <div className="filter-item search">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="جستجو: شماره سفارش، نام یا ایمیل کاربر و ..."/>
          </div>

          <div className="filter-item">
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">همه وضعیت‌ها</option>
              <option value="pending">در انتظار</option>
              <option value="initiated">شروع شده</option>
              <option value="paid">پرداخت‌شده</option>
              <option value="shipped">ارسال‌شده</option>
              <option value="completed">تکمیل‌شده</option>
              <option value="canceled">لغوشده</option>
              <option value="failed">ناموفق</option>
            </select>
          </div>

          <div className="filter-item">
            <select value={delivery} onChange={(e) => { setDelivery(e.target.value); setPage(1); }}>
              <option value="">روش ارسال (همه)</option>
              <option value="post">پست</option>
              <option value="snapp">اسنپ</option>
              <option value="express">تیپاکس</option>
            </select>
          </div>

          <div className="filter-item small">
            <input placeholder="شهر" value={city} onChange={(e) => setCity(e.target.value)} />
          </div>

          <div className="filter-item small">
            <input placeholder="استان" value={province} onChange={(e) => setProvince(e.target.value)} />
          </div>
        </div>

        <div className="filters-row">
          <div className="filter-item">
            <label>تاریخ از</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="filter-item">
            <label>تا</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>

          <div className="filter-item small">
            <label>حداقل مبلغ</label>
            <input type="number" placeholder="مثال: 100000" value={minTotal} onChange={(e) => setMinTotal(e.target.value)} />
          </div>

          <div className="filter-item small">
            <label>حداکثر مبلغ</label>
            <input type="number" placeholder="مثال: 1000000" value={maxTotal} onChange={(e) => setMaxTotal(e.target.value)} />
          </div>

          <div className="filter-item">
            <label>مرتب‌سازی</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="created_at">تاریخ ایجاد</option>
              <option value="total">مبلغ کل</option>
              <option value="status">وضعیت</option>
            </select>
            <select value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
              <option value="desc">نزولی</option>
              <option value="asc">صعودی</option>
            </select>
          </div>

          <div className="filter-item right">
            <label>تعداد / صفحه</label>
            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        <div className="filters-footer">
          <button className="btn-primary" onClick={() => { setPage(1); fetchOrders(); }}>اعمال فیلتر</button>
          <button className="btn-quiet" onClick={() => setExpandedFilters((s) => !s)}>{expandedFilters ? "بستن" : "باز کردن فیلترها"}</button>
        </div>
      </section>

      <section className="bulk-bar">
        <div>
          <input type="checkbox" checked={selectAllOnPage} onChange={toggleSelectAll} /> انتخاب همه در این صفحه
          <span className="muted"> ({selectedIds.size} انتخاب شده)</span>
        </div>

        <div className="bulk-actions">
          <button disabled={selectedIds.size === 0 || bulkActionLoading} onClick={() => bulkSetStatus("paid")} className="btn-primary small">نشانه‌گذاری به عنوان پرداخت‌شده</button>
          <button disabled={selectedIds.size === 0 || bulkActionLoading} onClick={() => bulkSetStatus("canceled")} className="btn-quiet small">لغو</button>
          <button disabled={selectedIds.size === 0 || bulkActionLoading} onClick={bulkDelete} className="btn-danger small">حذف گروهی</button>
        </div>
      </section>

      <main className="orders-list">
        {loading ? (
          <div className="loading">در حال بارگذاری...</div>
        ) : orders.length === 0 ? (
          <div className="empty">هیچ سفارشی مطابق فیلتر یافت نشد.</div>
        ) : viewMode === "cards" ? (
          <div className="cards-grid">
            {orders.map((order) => (
              <article key={order.id} className="order-card">
                <div className="card-top">
                  <div className="card-left">
                    <input type="checkbox" checked={selectedIds.has(order.id)} onChange={() => toggleSelect(order.id)} />
                    <div className="order-id">#{order.id?.toLocaleString?.("fa-IR") || order.id}</div>
                    <div className="order-user">{order.user?.name || "کاربر ناشناس"} <small className="muted">({order.user?.email || "-"})</small></div>
                  </div>

                  <div className="card-right">
                    <div className={`uop-status ${order.status || "unknown"}`}>{statusMap[order?.status] || order?.status || "—"}</div>
                    <div className="order-total">{fmt(order.total)} تومان</div>
                  </div>
                </div>

                <div className="card-body">
                  <div><strong>دریافت‌کننده:</strong> {order.recipient_name || order.user?.name || "-"}</div>
                  <div><strong>آدرس:</strong> {order.user?.street || order.address || "-"}</div>
                  <div><strong>شهر / استان:</strong> {(order.user?.city || "-")} / {(order.user?.province || "-")}</div>
                  <div className="items-preview"><strong>محصولات:</strong> {(order.items || []).slice(0,3).map(it=>it.product?.title || it.name).join(", ")}{(order.items||[]).length>3?` و ${ (order.items||[]).length - 3 } مورد دیگر...`:""}</div>
                </div>

                <div className="card-footer">
                  <div className="footer-left">
                    <label>روش ارسال: <strong>{deliveryMap[order?.delivery_option] || order?.delivery_option || "—"}</strong></label>
                    <label>هزینه ارسال: <strong>{fmt(order.shipping_cost)} تومان</strong></label>
                  </div>

                  <div className="footer-actions">
                    <select value={order.status} onChange={(e)=>updateStatus(order.id, e.target.value)}>
                      <option value="pending">در انتظار</option>
                      <option value="initiated">شروع شده</option>
                      <option value="paid">پرداخت‌شده</option>
                      <option value="shipped">ارسال‌شده</option>
                      <option value="completed">تکمیل‌شده</option>
                      <option value="canceled">لغوشده</option>
                    </select>
                    <button onClick={()=> window.open(`/admin/orders/${order.id}`)} className="btn-ghost small">مشاهده</button>
                    <button onClick={()=> deleteOrder(order.id)} className="btn-danger small">حذف</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <table className="orders-table">
            <thead>
              <tr>
                <th></th>
                <th>شمارۀ سفارش</th>
                <th>کاربر</th>
                <th>وضعیت</th>
                <th>مبلغ</th>
                <th>روش ارسال</th>
                <th>تاریخ</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td><input type="checkbox" checked={selectedIds.has(o.id)} onChange={() => toggleSelect(o.id)} /></td>
                  <td>#{o.id?.toLocaleString("fa-IR") || o.id}</td>
                  <td>{o.user?.name} <div className="muted small">{o.user?.email}</div></td>
                  <td><div className={`uop-status ${o.status}`}>{o.status}</div></td>
                  <td>{fmt(o.total)} تومان</td>
                  <td>{o.delivery_option || "-"}</td>
                  <td>{o.created_at ? new Date(o.created_at).toLocaleString("fa-IR") : "-"}</td>
                  <td className="actions-cell">
                    <select value={o.status} onChange={(e)=>updateStatus(o.id, e.target.value)}>
                      <option value="pending">در انتظار</option>
                      <option value="initiated">شروع شده</option>
                      <option value="paid">پرداخت‌شده</option>
                      <option value="shipped">ارسال‌شده</option>
                      <option value="completed">تکمیل‌شده</option>
                      <option value="canceled">لغوشده</option>
                    </select>
                    <button onClick={()=> window.open(`/admin/orders/${o.id}`)} className="btn-ghost small">نمایش</button>
                    <button onClick={()=> deleteOrder(o.id)} className="btn-danger small">حذف</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>

      <nav className="pagination">
        <div className="page-info">
          {totalCount ? `نمایش ${((page-1)*perPage)+1}-${Math.min(page*perPage, totalCount)} از ${totalCount}` : `نمایش صفحه ${page}`}
        </div>

        <div className="page-controls">
          <button onClick={()=>{ if(page>1) setPage(page-1); }} disabled={page<=1}>قبلی</button>
          <span>صفحه {page} از {lastPage}</span>
          <button onClick={()=>{ if(page<lastPage) setPage(page+1); }} disabled={page>=lastPage}>بعدی</button>
        </div>
      </nav>
    </div>
  );
}
